import { gcUserApi, GCUserVo } from '@/axios/user';
import CommonTable from '@/components/Table';
import OperatorBtns from '@/components/Table/operateBtns';
import CommonModalFactory from '@/plugins/ModalFactory';
import { timeFormatToYearSecond } from '@/utils/date';
import { useRequest, useSetState } from 'ahooks';
import { Button, Form, Input, message, Select } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { openDeleteModal } from '@/components/Modal/common';
import { useEffect } from 'react';

const EnterpriseAccount = ({ companyId }: { companyId: string }) => {
  const [params, setParams] = useSetState({
    companyId,
    pageIndex: 1,
    pageSize: 10,
  });
  const {
    data,
    loading,
    runAsync: refreshList,
  } = useRequest(() => gcUserApi.list({ ...params, companyId }), {
    refreshDeps: [params, companyId],
  });

  useEffect(() => {
    setParams({ companyId });
  }, [companyId, setParams]);

  const columns: ColumnsType<GCUserVo> = [
    {
      title: '账号名称',
      dataIndex: 'username',
      width: 180,
      render: (_, record) => record.username || record.accountName || record.nickname || '-',
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      width: 220,
      render: (value) => value || '-',
    },
    {
      title: '角色',
      dataIndex: 'roles',
      width: 180,
      render: (_, record) => {
        const roleNames = (record.roles || [])
          .map((role) => role.roleName || role.roleCode)
          .filter(Boolean);
        return roleNames.length > 0 ? roleNames.join('、') : record.roleName || '-';
      },
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      width: 180,
      render: (value) => (value ? timeFormatToYearSecond(String(value)) : '-'),
    },
    {
      title: '操作',
      width: 260,
      render: (_, record) => (
        <OperatorBtns
          btns={[
            {
              type: 'common',
              text: '编辑',
              onClick: () => {
                openEditEnterpriseAccountModal(
                  {
                    user: record,
                  },
                  undefined,
                  () => {
                    refreshList();
                  }
                );
              },
            },
            {
              type: 'common',
              text: '重置密码',
              danger: true,
              onClick: () => {
                openResetPasswordModal(
                  {
                    userId: record.userId ?? record.id,
                    username: record.username || record.accountName || record.nickname || '-',
                  },
                  undefined,
                  () => {
                    refreshList();
                  }
                );
              },
            },
            {
              type: 'delete',
              onClick: () => {
                openDeleteModal(
                  {
                    content: '确认删除该企业账号吗？',
                    api: async () => {
                      await gcUserApi.delete({
                        userId: record.userId,
                        id: record.id ?? record.userId,
                      });
                    },
                  },
                  undefined,
                  () => {
                    refreshList();
                  }
                );
              },
            },
          ]}
        />
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Button
          type="primary"
          onClick={() => {
            openCreateEnterpriseAccountModal(
              {
                companyId,
              },
              undefined,
              () => {
                setParams({ pageIndex: 1 });
              }
            );
          }}
        >
          创建账号
        </Button>
      </div>
      <CommonTable<GCUserVo>
        rowKey={(record) =>
          String(record.userId ?? record.id ?? record.email ?? record.username ?? record.accountName ?? record.nickname ?? '')
        }
        loading={loading}
        columns={columns}
        dataSource={data?.records || []}
        total={data?.total}
        params={params}
        setParams={setParams}
      />
    </div>
  );
};

export default EnterpriseAccount;

const openCreateEnterpriseAccountModal = CommonModalFactory<{
  companyId: string;
}>(
  {
    title: '创建企业账号',
    width: 520,
  },
  ({ data, setOkFn }) => {
    const [form] = Form.useForm();
    const { data: roleList = [], loading: roleListLoading } = useRequest(() => gcUserApi.roles());

    useEffect(() => {
      setOkFn(async () => {
        const values = await form.validateFields();
        await gcUserApi.create({
          companyId: data?.companyId,
          username: values.accountName,
          email: values.email,
          passwordHash: values.passwordHash,
          roleIds: [values.roleId],
        });
        message.success('创建成功');
      });
    }, [data?.companyId, form, setOkFn]);

    return (
      <Form form={form} layout="vertical">
        <Form.Item label="账号名称" name="accountName" rules={[{ required: true, message: '请输入账号名称' }]}>
          <Input placeholder="请输入账号名称" />
        </Form.Item>
        <Form.Item
          label="邮箱"
          name="email"
          rules={[
            { required: true, message: '请输入邮箱' },
            { type: 'email', message: '请输入有效的邮箱地址' },
          ]}
        >
          <Input placeholder="请输入邮箱" />
        </Form.Item>
        <Form.Item
          label="密码"
          name="passwordHash"
          rules={[
            { min: 6, message: '密码至少为六位' },
          ]}
        >
          <Input.Password placeholder="请输入密码" />
        </Form.Item>
        <Form.Item label="角色" name="roleId" rules={[{ required: true, message: '请选择角色' }]}>
          <Select
            placeholder="请选择角色"
            loading={roleListLoading}
            options={roleList.map((role) => ({
              value: role.roleId as string | number,
              label: role.roleName || role.roleCode || String(role.roleId || ''),
            }))}
          />
        </Form.Item>
      </Form>
    );
  }
);

const openEditEnterpriseAccountModal = CommonModalFactory<{
  user: GCUserVo;
}>(
  {
    title: '编辑企业账号',
    width: 520,
  },
  ({ data, setOkFn }) => {
    const [form] = Form.useForm();
    const { data: roleList = [], loading: roleListLoading } = useRequest(() => gcUserApi.roles());

    useEffect(() => {
      const user = data?.user;
      if (!user) {
        return;
      }
      const roleIds = (user.roles || [])
        .map((role) => role.roleId)
        .filter((roleId): roleId is number => roleId !== undefined && roleId !== null);
      form.setFieldsValue({
        username: user.username || user.accountName,
        email: user.email,
        status: user.status ?? 1,
        remark: user.remark,
        roleIds: roleIds.length > 0 ? roleIds : user.roleId !== undefined ? [user.roleId] : [],
      });
    }, [data?.user, form]);

    useEffect(() => {
      setOkFn(async () => {
        const values = await form.validateFields();
        const userId = data?.user?.userId ?? data?.user?.id;
        if (userId === undefined || userId === null) {
          throw new Error('userId is required');
        }
        await gcUserApi.update({
          userId,
          username: values.username,
          email: values.email,
          status: values.status,
          remark: values.remark,
          roleIds: values.roleIds,
        });
        message.success('编辑成功');
      });
    }, [data?.user, form, setOkFn]);

    return (
      <Form form={form} layout="vertical">
        <Form.Item label="账号名称" name="username" rules={[{ required: true, message: '请输入账号名称' }]}>
          <Input placeholder="请输入账号名称" />
        </Form.Item>
        <Form.Item label="邮箱" name="email" rules={[{ type: 'email', message: '请输入有效的邮箱地址' }]}>
          <Input placeholder="请输入邮箱" />
        </Form.Item>
        <Form.Item label="状态" name="status" rules={[{ required: true, message: '请选择状态' }]}>
          <Select
            placeholder="请选择状态"
            options={[
              { value: 1, label: '启用' },
              { value: 0, label: '禁用' },
            ]}
          />
        </Form.Item>
        <Form.Item label="角色" name="roleIds" rules={[{ required: true, message: '请选择角色' }]}>
          <Select
            mode="multiple"
            placeholder="请选择角色"
            loading={roleListLoading}
            options={roleList.map((role) => ({
              value: role.roleId as string | number,
              label: role.roleName || role.roleCode || String(role.roleId || ''),
            }))}
          />
        </Form.Item>
        <Form.Item label="备注" name="remark">
          <Input.TextArea placeholder="请输入备注" rows={3} />
        </Form.Item>
      </Form>
    );
  }
);

const openResetPasswordModal = CommonModalFactory<{
  userId?: number | string;
  username?: string;
}>(
  {
    title: '重置密码',
    width: 520,
  },
  ({ data, setOkFn }) => {
    const [form] = Form.useForm();

    useEffect(() => {
      setOkFn(async () => {
        const values = await form.validateFields();
        if (!data?.userId) {
          throw new Error('userId is required');
        }
        await gcUserApi.resetPassword({
          userId: data.userId,
          newPassword: values.newPassword,
        });
        message.success('重置成功');
      });
    }, [data?.userId, form, setOkFn]);

    return (
      <Form form={form} layout="vertical">
        <Form.Item label="账号名称">
          <Input value={data?.username} disabled />
        </Form.Item>
        <Form.Item
          label="新密码"
          name="newPassword"
          rules={[
            { required: true, message: '请输入新密码' },
            { min: 6, message: '密码至少为六位' },
          ]}
        >
          <Input.Password placeholder="请输入新密码" />
        </Form.Item>
      </Form>
    );
  }
);
